import { supabase } from './supabase';
import { Product, ProductCost } from '../types';

class ProductService {
  private static instance: ProductService;

  private constructor() {}

  public static getInstance(): ProductService {
    if (!ProductService.instance) {
      ProductService.instance = new ProductService();
    }
    return ProductService.instance;
  }

  public async createProduct(product: Omit<Product, 'id' | 'created_at'>): Promise<Product> {
    console.log('Création du produit:', product);

    // Assurer que is_pack est correctement défini
    const productToCreate = {
      ...product,
      is_pack: product.category === 'packs'
    };

    const { data, error } = await supabase
      .from('products')
      .insert([{
        name: productToCreate.name,
        description: productToCreate.description,
        price: productToCreate.price,
        image: productToCreate.image_url,
        category: productToCreate.category,
        stock: productToCreate.stock,
        is_available: productToCreate.is_available,
        is_pack: productToCreate.is_pack,
        costs: productToCreate.costs
      }])
      .select()
      .single();

    if (error) {
      console.error('Erreur lors de la création du produit:', error);
      throw error;
    }

    console.log('Produit créé avec succès:', data);

    // Si c'est un pack, créer l'entrée dans pack_items
    if ((productToCreate.is_pack || productToCreate.category === 'packs') && 
        productToCreate.pack_items && productToCreate.pack_items.length > 0) {
      console.log('Tentative de création des articles du pack:', {
        pack_id: data.id,
        items: productToCreate.pack_items
      });

      try {
        const { data: packItemsData, error: packItemsError } = await supabase
          .from('pack_items')
          .insert([{
            pack_id: data.id,
            items: productToCreate.pack_items
          }])
          .select();

        if (packItemsError) {
          console.error('Erreur détaillée lors de la création des articles du pack:', {
            error: packItemsError,
            pack_id: data.id,
            items: productToCreate.pack_items
          });
          throw packItemsError;
        }

        console.log('Articles du pack créés avec succès:', packItemsData);
      } catch (err) {
        console.error('Erreur lors de la création des articles du pack:', err);
        // On ne throw pas l'erreur ici pour ne pas bloquer la création du produit
      }
    }

    return {
      ...data,
      image_url: data.image,
      pack_items: productToCreate.pack_items || []
    };
  }

  public async updateProduct(id: string, product: Partial<Product>): Promise<Product> {
    console.log('Mise à jour du produit:', { id, product });

    // Assurer que is_pack est correctement défini si la catégorie est fournie
    const productToUpdate = {
      ...product,
      is_pack: product.category === 'packs' ? true : product.is_pack
    };

    const { data, error } = await supabase
      .from('products')
      .update({
        name: productToUpdate.name,
        description: productToUpdate.description,
        price: productToUpdate.price,
        image: productToUpdate.image_url,
        category: productToUpdate.category,
        stock: productToUpdate.stock,
        is_available: productToUpdate.is_available,
        is_pack: productToUpdate.is_pack,
        costs: productToUpdate.costs
      })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Erreur lors de la mise à jour du produit:', error);
      throw error;
    }

    console.log('Produit mis à jour avec succès:', data);

    // Si c'est un pack, mettre à jour les articles
    if ((productToUpdate.is_pack || productToUpdate.category === 'packs') && 
        productToUpdate.pack_items) {
      console.log('Tentative de mise à jour des articles du pack:', {
        pack_id: id,
        items: productToUpdate.pack_items
      });

      try {
        const { data: packItemsData, error: packItemsError } = await supabase
          .from('pack_items')
          .upsert([{
            pack_id: id,
            items: productToUpdate.pack_items
          }], {
            onConflict: 'pack_id'
          })
          .select();

        if (packItemsError) {
          console.error('Erreur détaillée lors de la mise à jour des articles du pack:', {
            error: packItemsError,
            pack_id: id,
            items: productToUpdate.pack_items
          });
          throw packItemsError;
        }

        console.log('Articles du pack mis à jour avec succès:', packItemsData);
      } catch (err) {
        console.error('Erreur lors de la mise à jour des articles du pack:', err);
        // On ne throw pas l'erreur ici pour ne pas bloquer la mise à jour du produit
      }
    }

    return {
      ...data,
      image_url: data.image,
      pack_items: productToUpdate.pack_items || []
    };
  }

  public async deleteProduct(id: string): Promise<void> {
    const { error } = await supabase
      .from('products')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Erreur lors de la suppression du produit:', error);
      throw error;
    }
  }

  public async getProduct(id: string): Promise<Product> {
    // Première requête pour récupérer le produit
    const { data: productData, error: productError } = await supabase
      .from('products')
      .select('*')
      .eq('id', id)
      .single();

    if (productError) {
      console.error('Erreur lors de la récupération du produit:', productError);
      throw productError;
    }

    console.log('Données du produit récupérées:', productData);

    // Si c'est un pack, récupérer les pack_items
    if (productData.is_pack) {
      const { data: packItemsData, error: packItemsError } = await supabase
        .from('pack_items')
        .select('*')
        .eq('pack_id', id)
        .single();

      if (packItemsError) {
        console.error('Erreur lors de la récupération des pack_items:', packItemsError);
        // On ne throw pas l'erreur ici pour ne pas bloquer le retour du produit
      }

      console.log('Pack items récupérés:', packItemsData);

      if (packItemsData?.items) {
        // Récupérer les détails de chaque article
        const itemsWithDetails = await Promise.all(
          packItemsData.items.map(async (item: { id: string; quantity: number }) => {
            const { data: itemData } = await supabase
              .from('products')
              .select('name, price, image')
              .eq('id', item.id)
              .single();
            
            return {
              ...item,
              name: itemData?.name || 'Article inconnu',
              price: itemData?.price || 0,
              image: itemData?.image || ''
            };
          })
        );

        return {
          ...productData,
          image_url: productData.image,
          pack_items: itemsWithDetails
        };
      }
    }

    return {
      ...productData,
      image_url: productData.image,
      pack_items: []
    };
  }

  public async getAllProducts(): Promise<Product[]> {
    const { data, error } = await supabase
      .from('products')
      .select(`
        *,
        pack_items (
          pack_id,
          items
        )
      `)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Erreur lors de la récupération des produits:', error);
      throw error;
    }

    return data.map(product => {
      // Si c'est un pack, s'assurer que pack_items est correctement formaté
      if (product.is_pack) {
        const packItems = product.pack_items?.[0]?.items || [];
        return {
          ...product,
          image_url: product.image,
          pack_items: packItems
        };
      }

      return {
        ...product,
        image_url: product.image,
        pack_items: []
      };
    });
  }

  public async toggleProductAvailability(id: string, is_available: boolean): Promise<Product> {
    const { data, error } = await supabase
      .from('products')
      .update({ is_available })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Erreur lors de la mise à jour de la disponibilité du produit:', error);
      throw error;
    }

    return data;
  }

  public async addProductCost(id: string, cost: ProductCost): Promise<Product> {
    const product = await this.getProduct(id);
    const updatedCosts = [...product.costs, cost].sort((a, b) => a.quantity - b.quantity);
    
    const { data, error } = await supabase
      .from('products')
      .update({ costs: updatedCosts })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Erreur lors de la mise à jour du coût du produit:', error);
      throw error;
    }

    return data;
  }

  public async removeProductCost(id: string, quantity: number): Promise<Product> {
    const product = await this.getProduct(id);
    const updatedCosts = product.costs.filter(cost => cost.quantity !== quantity);
    
    const { data, error } = await supabase
      .from('products')
      .update({ costs: updatedCosts })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Erreur lors de la suppression du coût du produit:', error);
      throw error;
    }

    return data;
  }

  public async updateProductCost(id: string, quantity: number, newCost: number): Promise<Product> {
    const product = await this.getProduct(id);
    const updatedCosts = product.costs.map(cost => 
      cost.quantity === quantity ? { ...cost, cost: newCost } : cost
    );
    
    const { data, error } = await supabase
      .from('products')
      .update({ costs: updatedCosts })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Erreur lors de la mise à jour du coût du produit:', error);
      throw error;
    }

    return data;
  }

  public getOptimalPurchaseQuantity(costs: ProductCost[], salesCount: number, sellingPrice: number): { quantity: number; cost: number; breakEvenPoint: number } {
    if (!costs.length) {
      return { quantity: 0, cost: 0, breakEvenPoint: 0 };
    }

    // Calculer le seuil de rentabilité pour chaque lot
    const lotsWithBreakEven = costs.map(cost => {
      const unitCost = cost.cost / cost.quantity;
      const breakEvenPoint = (cost.quantity * cost.cost) / sellingPrice;
      return {
        quantity: cost.quantity,
        cost: cost.cost,
        unitCost,
        breakEvenPoint
      };
    });

    // Filtrer les lots dont le seuil de rentabilité a été atteint
    const profitableLots = lotsWithBreakEven.filter(lot => lot.breakEvenPoint <= salesCount);

    if (!profitableLots.length) {
      return { quantity: 0, cost: 0, breakEvenPoint: 0 };
    }

    // Trouver le lot avec le coût unitaire le plus faible parmi les lots rentables
    const optimalLot = profitableLots.reduce((min, current) => 
      current.unitCost < min.unitCost ? current : min
    );

    return {
      quantity: optimalLot.quantity,
      cost: optimalLot.unitCost,
      breakEvenPoint: optimalLot.breakEvenPoint
    };
  }
}

export const productService = ProductService.getInstance(); 